import { FC, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { balanceService } from "services/balance/balance.service"

interface Props {
  open: boolean
  onClose: () => void
}

export const SettlementReportModal: FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [processors, setProcessors] = useState<string[]>([])
  const [methods, setMethods] = useState<string[]>([])
  const [processor, setProcessor] = useState("")
  const [paymentMethod, setPaymentMethod] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [format, setFormat] = useState<"pdf" | "csv">("pdf")
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open) {
      balanceService.listPaymentRouteProcessors().then((p) => {
        if (p) setProcessors(p)
      })
    }
  }, [open])

  useEffect(() => {
    if (open && processor) {
      balanceService.listPaymentRoutes({ processor, pageSize: 200 }).then((result) => {
        if (result) {
          const unique = Array.from(new Set(result.data.map((r) => r.payment_method))).sort()
          setMethods(unique)
          setPaymentMethod("")
        }
      })
    } else {
      setMethods([])
      setPaymentMethod("")
    }
  }, [open, processor])

  const handleSubmit = async () => {
    if (!processor || !startDate || !endDate) return

    setLoading(true)
    await balanceService.downloadSettlementReport({
      processor,
      payment_method: paymentMethod || undefined,
      start: new Date(startDate).toISOString(),
      end: new Date(endDate).toISOString(),
      format
    })
    setLoading(false)
    onClose()
  }

  const handleClose = () => {
    setProcessor("")
    setPaymentMethod("")
    setMethods([])
    setStartDate("")
    setEndDate("")
    setFormat("pdf")
    onClose()
  }

  return (
    <Modal open={open} handler={handleClose}>
      <div className="relative flex flex-col gap-4 modal-box min-w-[400px]">
        <h3 className="text-lg font-semibold">{t("balance.settlementReport")}</h3>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">{t("balance.processor")}</label>
          <select value={processor} onChange={(e) => setProcessor(e.target.value)} className="select select-bordered w-full">
            <option value="">{t("balance.selectProcessor")}</option>
            {processors.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">{t("balance.paymentMethodOptional")}</label>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            className="select select-bordered w-full"
            disabled={!processor}
          >
            <option value="">{t("balance.allMethods")}</option>
            {methods.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-4">
          <div className="flex flex-col gap-2 flex-1">
            <label className="text-sm font-medium">{t("balance.startDate")}</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input input-bordered w-full" />
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <label className="text-sm font-medium">{t("balance.endDate")}</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input input-bordered w-full" />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">{t("balance.format")}</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="format"
                value="pdf"
                checked={format === "pdf"}
                onChange={() => setFormat("pdf")}
                className="radio radio-primary"
              />
              PDF
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="format"
                value="csv"
                checked={format === "csv"}
                onChange={() => setFormat("csv")}
                className="radio radio-primary"
              />
              CSV
            </label>
          </div>
        </div>

        <div className="modal-action">
          <button className="btn btn-secondary" onClick={handleClose} disabled={loading}>
            {t("common.cancel")}
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading || !processor || !startDate || !endDate}>
            {loading ? t("common.loading") : t("balance.download")}
          </button>
        </div>
      </div>
    </Modal>
  )
}
